import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { useReadContracts } from "wagmi";
import { type Address } from "viem";
import { ERC721DropAbi } from "@atelier/abis";
import { ArtworkImage } from "@/components/ArtworkImage";
import { ipfsToHttp } from "@/lib/ipfs";
import { cn } from "@/lib/cn";

const GALLERY_CAP = 60;

type SimpleRead = { status: "success"; result: unknown } | { status: "failure" };

/**
 * Reads tokenURI(1..N) and ownerOf(1..N) on-chain, fetches each metadata
 * JSON, and renders the result as a grid that links into the per-token
 * detail page. Tokens whose ownerOf matches the connected wallet get a
 * "Yours" badge; tokens minted in the current session (passed via
 * `justMinted`) get an amber "Just minted" highlight.
 */
export function MintedGallery({
  shortName,
  address,
  chainId,
  totalMinted,
  maxSupply,
  ipfsGateway,
  wallet,
  justMinted,
}: {
  shortName: string;
  address: Address;
  chainId: number;
  totalMinted: number;
  maxSupply: bigint;
  ipfsGateway: string;
  wallet: Address | undefined;
  justMinted: ReadonlySet<string>;
}) {
  const visibleCount = Math.min(totalMinted, GALLERY_CAP);
  const tokenIds = useMemo(
    () => Array.from({ length: visibleCount }, (_, i) => BigInt(i + 1)),
    [visibleCount],
  );

  const tokenUriReads = useReadContracts({
    allowFailure: true,
    contracts: tokenIds.length > 0
      ? tokenIds.map((id) => ({
          address,
          abi: ERC721DropAbi,
          functionName: "tokenURI",
          args: [id],
          chainId,
        }))
      : [],
    query: { enabled: tokenIds.length > 0 },
  });

  const ownerReads = useReadContracts({
    allowFailure: true,
    contracts: tokenIds.length > 0
      ? tokenIds.map((id) => ({
          address,
          abi: ERC721DropAbi,
          functionName: "ownerOf",
          args: [id],
          chainId,
        }))
      : [],
    query: { enabled: tokenIds.length > 0, refetchInterval: 30_000 },
  });

  const tokenUriResults = ((tokenUriReads.data as unknown) ?? []) as readonly SimpleRead[];
  const ownerResults = (ownerReads.data as unknown) as readonly SimpleRead[] | undefined;

  const tokenMetaQueries = useQueries({
    queries: tokenUriResults.map((r, i) => {
      const uri = r.status === "success" ? (r.result as string) : null;
      const httpUri = uri ? ipfsToHttp(uri, ipfsGateway) : null;
      return {
        queryKey: ["tokenMeta", chainId, address, tokenIds[i]?.toString(), httpUri],
        queryFn: async () => {
          if (!httpUri) return null;
          const res = await fetch(httpUri);
          if (!res.ok) throw new Error(`metadata ${res.status}`);
          return (await res.json()) as { name?: string; image?: string };
        },
        enabled: !!httpUri,
        staleTime: 60_000,
        retry: 1,
      };
    }),
  });

  if (totalMinted === 0) return null;

  return (
    <section className="mt-10">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">Minted</h2>
        <span className="text-xs text-muted-foreground">
          {maxSupply === 0n
            ? `${totalMinted} so far`
            : `${totalMinted} of ${maxSupply.toString()}`}
          {totalMinted > GALLERY_CAP && ` · showing first ${GALLERY_CAP}`}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
        {tokenIds.map((id, i) => {
          const meta = tokenMetaQueries[i]?.data ?? null;
          const img = meta?.image ? ipfsToHttp(meta.image, ipfsGateway) : null;
          const tokenIdStr = id.toString();
          const ownerEntry = ownerResults?.[i];
          const owner =
            ownerEntry?.status === "success"
              ? (ownerEntry.result as string).toLowerCase()
              : null;
          const isYours = !!wallet && !!owner && owner === wallet.toLowerCase();
          const isFresh = justMinted.has(tokenIdStr);
          return (
            <Link
              key={tokenIdStr}
              to={`/${shortName}/${address}/${tokenIdStr}`}
              className={cn(
                "group relative overflow-hidden rounded-lg border bg-card transition",
                "hover:-translate-y-0.5 hover:shadow-md hover:border-foreground/40",
                isFresh
                  ? "border-amber-500 ring-2 ring-amber-400/60"
                  : isYours
                  ? "border-emerald-500/70"
                  : "border-border",
              )}
            >
              <div className="aspect-square w-full bg-muted">
                {img ? (
                  <ArtworkImage
                    alt={meta?.name ?? `#${tokenIdStr}`}
                    src={img}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                    #{tokenIdStr}
                  </div>
                )}
              </div>
              {isFresh && (
                <span className="absolute left-1 top-1 rounded-md bg-amber-500 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-950 shadow-sm">
                  Just minted
                </span>
              )}
              {!isFresh && isYours && (
                <span className="absolute left-1 top-1 rounded-md bg-emerald-500 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-950 shadow-sm">
                  Yours
                </span>
              )}
              <div className="px-2 py-1 text-[11px] text-muted-foreground">#{tokenIdStr}</div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
